<template>
  <div>
    <v-menu
      bottom
      left
      z-index="500"
    >
      <v-btn
        slot="activator"
        dark
        flat
        icon
        :loading="loading"
      >
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on">
              settings
            </v-icon>
          </template>
          <span>{{ $t('message.common.settings') }}</span>
        </v-tooltip>
      </v-btn>

      <v-list dense>
        <component
          :is="tile.component"
          v-for="tile in config.listTilesTools"
          :id="id"
          :key="tile.title"
          :relative-version-id="relativeVersionId"
          :config="tile"
          :type="type"
          @load="$event ? loadingStart() : loadingFinish()"
          v-on="$listeners"
        />

        <v-list-tile
          v-if="!editingTranslation && !versionable"
          ripple
          :disabled="!form.pristine"
          @click="showActionsLogModal = true"
        >
          <v-list-tile-action>
            <v-icon>history</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('message.entity.history.contentTitle') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>

        <v-list-tile
          v-if="versionable"
          ripple
          :disabled="!form.pristine"
          @click="showVersionSelector = true"
        >
          <v-list-tile-action>
            <v-icon>history</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('message.entity.history.contentTitle') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>

        <v-tooltip
          left
          :disabled="form.pristine"
        >
          <v-list-tile
            v-if="!editingTranslation"
            slot="activator"
            ripple
            :disabled="!form.pristine"
            @click="cloneContent"
          >
            <v-list-tile-action>
              <v-icon class="material-icons-outlined">
                difference
              </v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{ $t('message.content.tools.clone') }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <div>{{ $t('message.content.tools.duplicateTooltip') }}</div>
        </v-tooltip>

        <v-tooltip
          v-if="versionable"
          left
          :disabled="form.pristine"
        >
          <v-list-tile
            slot="activator"
            ripple
            :disabled="!form.pristine"
            @click="cloneContentVersion"
          >
            <v-list-tile-action>
              <v-icon>control_point_duplicate</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{ $t('message.content.tools.cloneVersion') }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <div>{{ $t('message.content.tools.cloneTooltip') }}</div>
        </v-tooltip>

        <v-list-tile
          v-if="downloadUrl"
          :href="downloadUrl"
          download
          target="_blank"
        >
          <v-list-tile-action>
            <v-icon>cloud_download</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('message.content.tools.downloadUrl') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>

        <v-divider v-if="downloadUrl || !editingTranslation" />

        <v-list-tile
          v-if="published"
          v-authorized="{ action: 'unpublish', entity: 'content', id }"
          @click.stop="confirmUnpublish"
        >
          <v-list-tile-action>
            <v-icon>file_download</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('message.content.tools.unpublish') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>

        <v-list-tile
          v-else-if="!editingTranslation"
          v-authorized.destroy="{ action: 'delete', entity: 'content', id }"
          @click.stop="showConfirmDelete = true"
        >
          <v-list-tile-action>
            <v-icon>delete</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('message.content.tools.delete') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>

        <v-list-tile
          v-if="hasRedirection && !published"
          @click.stop="showRedirectMetadataDialog = true"
        >
          <v-list-tile-action>
            <v-icon>call_missed_outgoing</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('message.content.tools.checkRedirection') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>
    </v-menu>

    <v-layout
      row
      justify-center
    >
      <nx-redirect-metadata-dialog
        v-if="hasRedirection"
        :value="redirectMetadata"
        :open="showRedirectMetadataDialog"
        @dialog:close="showRedirectMetadataDialog = false"
      />

      <nx-redirect-dialog
        v-if="hasLandingPage"
        :open="showConfirmUnpublish"
        type="content"
        :config="redirectFormConfig"
        :value="redirectDraft"
        :loading="loading"
        :current-url="publicUrl"
        @input="onRedirectInput"
        @click:confirm="unpublishContent"
        @click:cancel="clearRedirectDraft"
      />

      <v-dialog
        v-else
        v-model="showConfirmUnpublish"
        persistent
        max-width="350"
      >
        <v-card>
          <v-card-title class="headline">
            {{ $t('message.content.tools.unpublishConfirm.title') }}
          </v-card-title>
          <v-card-text>
            {{ $t('message.content.tools.unpublishConfirm.text') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="info darken-1"
              flat
              @click.native="showConfirmUnpublish = false"
            >
              {{ $t('message.common.cancel') }}
            </v-btn>
            <v-btn
              color="error darken-1"
              flat
              :loading="loading"
              @click.native="unpublishContent"
            >
              {{ $t('message.content.tools.unpublish') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>
    <v-layout
      row
      justify-center
    >
      <v-dialog
        v-model="showConfirmDelete"
        persistent
        max-width="350"
      >
        <v-card>
          <v-card-title class="headline">
            {{ $t('message.content.tools.deleteConfirm.title') }}
          </v-card-title>
          <v-card-text>
            {{ $t('message.content.tools.deleteConfirm.text') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="info darken-1"
              flat
              @click.native="showConfirmDelete = false"
            >
              {{ $t('message.common.cancel') }}
            </v-btn>
            <v-btn
              color="error darken-1"
              flat
              :loading="loading"
              @click.native="deleteContent();"
            >
              {{ $t('message.content.tools.delete') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>

    <nx-content-editor-actions-log-tool
      v-model="showActionsLogModal"
      :editor-store-path="storePath"
    />

    <v-dialog
      v-model="showVersionSelector"
      scrollable
      lazy
      max-width="98vw"
    >
      <nx-content-version-navigator
        :id="id"
        :active="showVersionSelector"
        :language="language"
        :relative-version-id="relativeVersionId"
        @updateRelativeVersionId="updateRelativeVersionId"
        @close="showVersionSelector = false"
      />
    </v-dialog>
  </div>
</template>

<script>
import Storable from '@/mixins/storable';
import Loadable from '@/mixins/loadable';
import { toolsComponentConfig } from '@/modules/content/editor/config';
import NxContentEditorActionsLogTool from '@/modules/content/editor/tools/ActionsLogTool';
import NxRedirectDialog from '@/modules/redirect/components/RedirectDialog';
import NxRedirectMetadataDialog from '@/modules/redirect/components/RedirectMetadataDialog';
import redirectConfig, { createRedirectFormConfig } from '@/modules/redirect/config';
import Vue from 'vue';
import NxContentVersionNavigator from '@/modules/content/editor/components/version/ContentVersionNavigator';
import { getContentsWithLandingPage } from '../config';

export default {
  name: 'NxContentEditorTools',

  components: {
    NxContentVersionNavigator,
    NxContentEditorActionsLogTool,
    NxRedirectDialog,
    NxRedirectMetadataDialog,
  },
  mixins: [Storable, Loadable],
  inject: ['$getParentEditor'],

  props: {
    editorStorePath: {
      type: [String, Array],
      required: true,
    },
  },

  data() {
    return {
      showConfirmUnpublish: false,
      showConfirmDelete: false,
      showRedirectMetadataDialog: false,
      showVersionSelector: false,
      loadingTitle: 'message.content.tools.deleting',
      showActionsLogModal: false,
      redirectConfig,
      redirectFormConfig: createRedirectFormConfig(),
      redirectDraft: { ...redirectConfig.defaultValue },
    };
  },

  computed: {
    storePath() {
      return this.editorStorePath;
    },
    form() {
      return this.getters.form;
    },
    publicUrl() {
      return this.getters.publicUrl;
    },
    type() {
      return this.getters.type;
    },
    hasRedirection() {
      return this.getters.hasRedirection;
    },
    redirectMetadata() {
      return this.getters.redirectMetadata;
    },
    editingTranslation() {
      return this.getters.editingTranslation;
    },
    published() {
      if (this.state.entityPublished) {
        return true;
      }
      return this.state.versionPublished;
    },
    language() {
      return this.state.currentLanguage;
    },
    downloadUrl() {
      return this.state.downloadUrl;
    },
    id() {
      if (this.$getParentEditor()) {
        return this.$getParentEditor().id;
      }
      return parseInt(this.$route.params.id, 10);
    },
    relativeVersionId() {
      if (this.$getParentEditor()) {
        return this.$getParentEditor().relativeVersionId;
      }
      return parseInt(this.$route.params.relativeVersionId, 10);
    },
    config() {
      return toolsComponentConfig;
    },
    hasLandingPage() {
      return getContentsWithLandingPage().includes(this.type);
    },
    versionable() {
      return this.getters.versionable;
    },
  },

  methods: {
    onRedirectInput(field, value) {
      Vue.set(this.redirectDraft, field, value);
    },
    clearRedirectDraft() {
      this.showConfirmUnpublish = !this.showConfirmUnpublish;
      this.redirectDraft = { ...redirectConfig.defaultValue };
    },
    async cloneContent() {
      this.loadingStart();
      await this.dispatch('CLONE');
      this.loadingFinish();
    },
    async cloneContentVersion() {
      this.loadingStart();
      const { relativeVersionId } = await this.dispatch('CLONE_VERSION');
      if (relativeVersionId) {
        this.$emit('updateRelativeVersionId', relativeVersionId);
      }
      this.loadingFinish();
    },
    confirmUnpublish() {
      this.showConfirmUnpublish = true;
    },
    async unpublishContent() {
      this.loadingStart();
      try {
        const { latestRelativeVersionId } = await this.dispatch('UNPUBLISH', this.redirectDraft);
        if (latestRelativeVersionId) {
          this.updateRelativeVersionId(latestRelativeVersionId);
        }
        this.$emit('reload:editor');
      } finally {
        this.clearRedirectDraft();
        this.loadingFinish();
      }
    },
    async deleteContent() {
      this.loadingStart();
      await this.dispatch('DELETE');
      this.showConfirmDelete = false;
      this.loadingFinish();
    },
    updateRelativeVersionId(relativeVersionId) {
      this.$emit('updateRelativeVersionId', relativeVersionId);
    },
  },
};
</script>
